import { UserDeviceEnum } from "@/__generated__/graphql";
import { showToast } from "@/components/ui/toast/show-toast";
import { collectError } from "../monitoring/sentry";
import { getCookie, setCookie } from "../cookie";
import { app } from "@/config/env";
import { GA_ID } from "@/constants";

export const getRudderSession = () => {
  const rudderGAID = app.RUDDERSTACK.GA_ID || "";
  const gaIdNumber = rudderGAID?.split("-")?.[1];
  const appLuceCookieValue = getCookie(`_ga_${gaIdNumber}`);
  const luceSGCookieValue = getCookie("rudder_ga_session");
  return luceSGCookieValue || appLuceCookieValue || "";
};

const extractClientId = (str: string) => {
  if (!str) return "";
  const regex = /^GA\d\.\d\.(\d+\.\d+)$/;
  const match = str?.match(regex);
  return match ? match[1] : "";
};

export const getRudderClientId = () => {
  return extractClientId(getCookie(GA_ID) || "");
};

export const storeSessionFromUrlToCookie = () => {
  const params = new URL(window.location.href).searchParams;
  const rudder_ga_session = params.get("rudder_ga_session") || "";
  if (rudder_ga_session) setCookie("rudder_ga_session", rudder_ga_session);
};

export const handleWhatsappNumber = (number: string, template = "") => {
  const phone = number.replace(/\D/g, "");
  handleOpenLink("//wa.me/" + phone + "?text=" + template);
};

export const getUserDevice = () => {
  return UserDeviceEnum.Browser;
};
export const getAnalyticDevice = () => {
  return "web";
};
export function makeFileFromPicker(fileInfo: File, customFilename?: string) {
  const fileName = fileInfo.name.replace(/\.[^/.]+$/, "");
  const type = fileInfo.type.split("/");
  const format = type[1];

  const file = new File(
    [fileInfo],
    customFilename ? `${customFilename}.${format}` : `${fileName}.${format}`,
    { type: fileInfo.type },
  );

  return file;
}

export const handleOpenLink = async (url: string) => {
  window.open(url, "_blank");
  return Promise.resolve(true);
};

export const share = (url: string, message = url) => {
  if (!navigator.canShare) {
    showToast({
      type: "error",
      title: "Your browser doesn't support the Web Share API.",
    });
    return;
  }

  navigator.share({ url, text: message }).catch((error) => {
    if (error instanceof Error) {
      const errString = error.toString();
      if (
        errString.includes("AbortError") ||
        errString.includes("InvalidStateError")
      ) {
        // ignore when user cancel share
        return;
      } else if (errString.includes("NotAllowedError")) {
        showToast({
          type: "error",
          title:
            "Sharing failed due to blocked permissions, inactive window, or security restrictions. Please check your settings and try again",
        });
      }
    }

    collectError(error, {
      tags: {
        feature: "web_share",
      },
    });
  });
};

export const downloadFromUrl = async (url: string, fileName: string) => {
  // TODO: handle download from url correctly
  try {
    // Fetch the file as a blob to ensure download works for images and cross-origin URLs
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) throw new Error("Network response was not ok");
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const downloadLink = document.createElement("a");
    downloadLink.href = blobUrl;
    downloadLink.download = fileName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    // Clean up the blob URL after download
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);

    return Promise.resolve();
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

export const downloadAndShare = async (
  downloadUri: string,
  fileName: string = "image.jpg",
) => {
  if (!navigator.share) {
    showToast({
      type: "error",
      title: "Your browser doesn't support the Web Share API.",
    });
    return Promise.resolve(false);
  }

  return navigator
    .share({
      url: downloadUri,
      text: `Download ${fileName}`,
    })
    .then(() => {
      return true;
    })
    .catch((error) => {
      if (error instanceof Error) {
        const errString = error.toString();
        if (
          errString.includes("AbortError") ||
          errString.includes("InvalidStateError")
        ) {
          // ignore when user cancel share
        } else if (errString.includes("NotAllowedError")) {
          showToast({
            type: "error",
            title:
              "Sharing failed due to blocked permissions, inactive window, or security restrictions. Please check your settings and try again",
          });
        }
        return false;
      }

      collectError(error, {
        tags: {
          feature: "web_share",
        },
      });
      return false;
    });
  return true;
};
